import { useState, useEffect } from "react";
import { db } from "../firebase/config";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { STAGES, STAGE_COLOR, inr, todayStr } from "../crm/theme";
import { fetchLeads, fetchTasks, visibleTo, leadRevenue } from "../crm/crmApi";
import { fmtD } from "../crm/dateFormat";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function formatRupees(n) {
  return "₹" + Math.round(n).toLocaleString("en-IN");
}

function CrmOverview({ user }) {
  const [leads, setLeads] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [allLeads, allTasks] = await Promise.all([fetchLeads(), fetchTasks()]);
        setLeads(visibleTo(allLeads, user));
        setTasks(visibleTo(allTasks, user));
      } catch (e) {
        console.error("CRM overview load failed:", e);
      }
      setLoading(false);
    })();
  }, [user]);

  const card = { background: "#1a1a24", border: "1px solid #2d2d3a", borderRadius: 12, padding: "clamp(12px, 3vw, 20px)" };
  const grid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: 24 };
  const num = { fontSize: "clamp(22px, 6vw, 32px)", fontWeight: 700, color: "#8b7cff", margin: "6px 0" };
  const label = { fontSize: 13, color: "#8f95a8" };
  const sectionTitle = { fontSize: 16, fontWeight: 700, color: "#e8e8f0", marginBottom: 12 };

  if (loading) return null;

  const activeLeads = leads.filter((l) => l.stage !== "Won" && l.stage !== "Lost");
  const wonLeads = leads.filter((l) => l.stage === "Won");
  const pipelineValue = activeLeads.reduce((s, l) => s + leadRevenue(l), 0);
  const conversionPct = leads.length > 0 ? Math.round((wonLeads.length / leads.length) * 100) : null;
  const today = todayStr();
  const upcomingTasks = tasks
    .filter((t) => t.status !== "done")
    .sort((a, b) => (a.dueDate || "").localeCompare(b.dueDate || ""))
    .slice(0, 6);

  const stageData = STAGES.filter((s) => s !== "Lost").map((s) => ({
    stage: s.length > 10 ? s.slice(0, 9) + "…" : s,
    value: leads.filter((l) => l.stage === s).reduce((sum, l) => sum + leadRevenue(l), 0),
    fill: STAGE_COLOR[s],
  }));

  return (
    <div style={{ marginBottom: 32 }}>
      <h2 style={{ color: "#a78bfa", fontSize: 18, marginBottom: 16 }}>🧭 CRM Overview</h2>
      <div style={grid}>
        <div style={card}>
          <div style={label}>Open Pipeline Value</div>
          <div style={num}>{inr(pipelineValue)}</div>
          <div style={label}>{activeLeads.length} active lead{activeLeads.length === 1 ? "" : "s"}</div>
        </div>
        <div style={card}>
          <div style={label}>Deals Won</div>
          <div style={num}>{wonLeads.length}</div>
          <div style={label}>{inr(wonLeads.reduce((s, l) => s + leadRevenue(l), 0))} won revenue</div>
        </div>
        <div style={card}>
          <div style={label}>Conversion Rate</div>
          <div style={num}>{conversionPct !== null ? `${conversionPct}%` : "—"}</div>
          <div style={label}>Won of all leads created</div>
        </div>
        <div style={card}>
          <div style={label}>Open Tasks</div>
          <div style={num}>{tasks.filter((t) => t.status !== "done").length}</div>
          <div style={label}>{tasks.filter((t) => t.status !== "done" && t.dueDate && t.dueDate < today).length} overdue</div>
        </div>
      </div>

      {leads.length > 0 && (
        <div style={{ ...card, marginBottom: 24, height: 260 }}>
          <div style={sectionTitle}>Pipeline Value by Stage</div>
          <ResponsiveContainer width="100%" height="85%">
            <BarChart data={stageData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2d2d3a" />
              <XAxis dataKey="stage" stroke="#8f95a8" fontSize={11} />
              <YAxis stroke="#8f95a8" fontSize={11} />
              <Tooltip contentStyle={{ background: "#1a1a24", border: "1px solid #2d2d3a", color: "#e8e8f0" }} formatter={(v) => inr(v)} />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {stageData.map((d, i) => <Cell key={i} fill={d.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {upcomingTasks.length > 0 && (
        <div style={{ ...card, marginBottom: 24 }}>
          <div style={sectionTitle}>Upcoming Tasks</div>
          {upcomingTasks.map((t) => {
            const overdue = t.dueDate && t.dueDate < today;
            return (
              <div key={t.id} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #22222c", fontSize: 13 }}>
                <span style={{ color: "#e8e8f0" }}>{t.title}</span>
                <span style={{ color: overdue ? "#ff7b7b" : "#8f95a8" }}>{fmtD(t.dueDate)}{overdue ? " (overdue)" : ""}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Dashboard({ user }) {
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState("");
  const [expandedRep, setExpandedRep] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const societiesSnap = await getDocs(collection(db, "societies"));
        const societies = societiesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

        // Revenue is estimated from the agreed price at signup (settings/init.pricing),
        // not from actual collected payments — there's no billing/invoice data to pull
        // from yet. Only "active" societies count toward revenue; trials are unbilled.
        const pricingDocs = await Promise.all(
          societies.map((s) => getDoc(doc(db, "societies", s.id, "settings", "init")))
        );
        societies.forEach((s, i) => {
          const settings = pricingDocs[i].exists() ? pricingDocs[i].data() : {};
          const pricing = settings.pricing;
          s.priceMonthly = pricing && pricing.type === "tier" ? Number(pricing.priceMonthly) || 0 : 0;
          s.revenue = s.status === "active" ? s.priceMonthly : 0;
        });

        const byStatus = societies.reduce((acc, s) => {
          acc[s.status || "unknown"] = (acc[s.status || "unknown"] || 0) + 1;
          return acc;
        }, {});
        const draft = byStatus.draft || 0;
        const trial = byStatus.trial || 0;
        const active = byStatus.active || 0;
        const trialOrActive = trial + active;
        const conversionPct = trialOrActive > 0 ? Math.round((active / trialOrActive) * 100) : null;
        const totalRevenue = societies.reduce((sum, s) => sum + s.revenue, 0);

        const now = Date.now();
        const trialsEndingSoon = societies
          .filter((s) => s.status === "trial" && s.trialEndsAt)
          .map((s) => ({ ...s, daysLeft: Math.ceil((new Date(s.trialEndsAt).getTime() - now) / MS_PER_DAY) }))
          .filter((s) => s.daysLeft >= 0 && s.daysLeft <= 7)
          .sort((a, b) => a.daysLeft - b.daysLeft);

        const inProgress = societies
          .filter((s) => s.status === "draft")
          .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

        const byRep = {};
        for (const s of societies) {
          const key = s.createdByEmail || "Unattributed";
          if (!byRep[key]) byRep[key] = { key, name: s.createdByName || key, draft: 0, trial: 0, active: 0, total: 0, revenue: 0, societies: [] };
          byRep[key].total++;
          byRep[key].revenue += s.revenue;
          byRep[key].societies.push(s);
          if (s.status === "draft") byRep[key].draft++;
          if (s.status === "trial") byRep[key].trial++;
          if (s.status === "active") byRep[key].active++;
        }
        // Leaderboard — ranked by revenue, not just society count.
        const repRows = Object.values(byRep).sort((a, b) => b.revenue - a.revenue);

        setStats({
          totalSocieties: societies.length,
          draft, trial, active, conversionPct, totalRevenue,
          trialsEndingSoon, inProgress, repRows,
        });
      } catch (e) {
        console.error("Dashboard stats failed:", e);
        setErr("Couldn't load some stats — check your connection.");
      }
    })();
  }, []);

  const card = { background: "#1a1a24", border: "1px solid #2d2d3a", borderRadius: 12, padding: "clamp(12px, 3vw, 20px)" };
  const grid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: 24 };
  const num = { fontSize: "clamp(22px, 6vw, 32px)", fontWeight: 700, color: "#8b7cff", margin: "6px 0" };
  const label = { fontSize: 13, color: "#8f95a8" };
  const sectionTitle = { fontSize: 16, fontWeight: 700, color: "#e8e8f0", marginBottom: 12 };
  const th = { textAlign: "left", fontSize: 11, color: "#8f95a8", textTransform: "uppercase", padding: "8px 12px", borderBottom: "1px solid #2d2d3a" };
  const td = { padding: "10px 12px", fontSize: 13, color: "#e8e8f0", borderBottom: "1px solid #22222c" };

  return (
    <div style={{ padding: 24, background: "#0f0f13", minHeight: "100vh", color: "#fff" }}>
      <h1 style={{ marginTop: 0, color: "#6c63ff" }}>Adyantam Dashboard</h1>

      <CrmOverview user={user} />

      {err && <div style={{ color: "#ff7b7b", marginBottom: 16 }}>{err}</div>}

      {!stats ? (
        <div style={{ color: "#8f95a8" }}>Loading…</div>
      ) : (
        <>
          <div style={grid}>
            <div style={card}>
              <div style={label}>Societies onboarded</div>
              <div style={num}>{stats.totalSocieties}</div>
              <div style={label}>{stats.draft} draft · {stats.trial} trial · {stats.active} active</div>
            </div>

            <div style={card}>
              <div style={label}>Trial → Active conversion</div>
              <div style={num}>{stats.conversionPct !== null ? `${stats.conversionPct}%` : "—"}</div>
              <div style={label}>{stats.active} of {stats.trial + stats.active} trials converted</div>
            </div>

            <div style={card}>
              <div style={label}>Estimated monthly revenue</div>
              <div style={num}>{formatRupees(stats.totalRevenue)}</div>
              <div style={label}>From active societies' agreed pricing</div>
            </div>

            <div style={card}>
              <div style={label}>Trials ending within 7 days</div>
              <div style={num}>{stats.trialsEndingSoon.length}</div>
              <div style={label}>Follow up before they lapse</div>
            </div>
          </div>

          {stats.trialsEndingSoon.length > 0 && (
            <div style={{ ...card, marginBottom: 24 }}>
              <div style={sectionTitle}>Trials Ending Soon</div>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr><th style={th}>Society</th><th style={th}>Days Left</th></tr></thead>
                <tbody>
                  {stats.trialsEndingSoon.map((s) => (
                    <tr key={s.id}>
                      <td style={td}>{s.name || s.id}</td>
                      <td style={td}>{s.daysLeft === 0 ? "Today" : `${s.daysLeft} day${s.daysLeft === 1 ? "" : "s"}`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {stats.inProgress.length > 0 && (
            <div style={{ ...card, marginBottom: 24 }}>
              <div style={sectionTitle}>In-Progress Setups ({stats.inProgress.length})</div>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr><th style={th}>Society</th><th style={th}>Started By</th><th style={th}>Last Updated</th></tr></thead>
                <tbody>
                  {stats.inProgress.map((s) => (
                    <tr key={s.id}>
                      <td style={td}>{s.name || s.id}</td>
                      <td style={td}>{s.createdByName || "—"}</td>
                      <td style={td}>{s.createdAt ? fmtD(s.createdAt) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div style={card}>
            <div style={sectionTitle}>Sales Leaderboard</div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={th}>Rep</th><th style={th}>Draft</th><th style={th}>Trial</th>
                  <th style={th}>Active</th><th style={th}>Total</th><th style={th}>Revenue</th>
                </tr>
              </thead>
              <tbody>
                {stats.repRows.map((r) => (
                  <>
                    <tr key={r.key} onClick={() => setExpandedRep(expandedRep === r.key ? null : r.key)} style={{ cursor: "pointer" }}>
                      <td style={td}>{expandedRep === r.key ? "▾ " : "▸ "}{r.name}</td>
                      <td style={td}>{r.draft}</td>
                      <td style={td}>{r.trial}</td>
                      <td style={td}>{r.active}</td>
                      <td style={td}>{r.total}</td>
                      <td style={{ ...td, fontWeight: 700, color: "#8b7cff" }}>{formatRupees(r.revenue)}</td>
                    </tr>
                    {expandedRep === r.key && (
                      <tr key={r.key + "-detail"}>
                        <td colSpan="6" style={{ padding: 0, borderBottom: "1px solid #22222c" }}>
                          <table style={{ width: "100%", borderCollapse: "collapse", background: "#14141b" }}>
                            <thead>
                              <tr>
                                <th style={{ ...th, paddingLeft: 32 }}>Society</th><th style={th}>Setup ID</th><th style={th}>Status</th>
                                <th style={th}>Revenue</th><th style={th}>Trial Ends</th>
                              </tr>
                            </thead>
                            <tbody>
                              {r.societies.map((s) => (
                                <tr key={s.id}>
                                  <td style={{ ...td, paddingLeft: 32 }}>{s.name || s.id}</td>
                                  <td style={{ ...td, fontFamily: "monospace", color: "#9aa0b4" }}>{s.id}</td>
                                  <td style={td}>{s.status || "—"}</td>
                                  <td style={td}>{s.revenue > 0 ? formatRupees(s.revenue) : "—"}</td>
                                  <td style={td}>{s.trialEndsAt ? fmtD(s.trialEndsAt) : "—"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}